<script setup>
import { computed, ref } from 'vue';
import XlButton from '../xl/XlButton.vue';
import XlModal from '../xl/XlModal.vue';
import { saveInteractionData } from '../../helpers/interactionHelper';
import { leavesPlatform } from '../../helpers/platformLinks';
import { useAuthStore } from '../../stores/auth';

/**
 * Home's "Quick links" (XN-4691): the links added under Content → Links, the
 * one place a policyholder sees them (the Library does not). Things to do
 * rather than read. Newest first, five shown, the rest on asking: links live
 * nowhere else, so "Show all" opens the list here rather than going to a
 * page. Every row says where it goes and opens in a new tab; one that leaves
 * the platform says so first (Hunter, 2026-10-07: only when actually leaving).
 * A followed link is counted for the Content page. Prototype `.sh` +
 * `.card .row` and `openLink()` (build 30 Sep).
 */
const props = defineProps({ links: { type: Array, required: true } });

const SHOWN = 5;
const auth = useAuthStore();
const expanded = ref(false);

const newestFirst = computed(() => [...props.links].sort((a, b) => b.id - a.id));
const rows = computed(() => (expanded.value ? newestFirst.value : newestFirst.value.slice(0, SHOWN)));

const domainOf = (link) => {
  try {
    return new URL(link.link).host;
  } catch {
    return '';
  }
};

// Analytics must never surface as an unhandled rejection; saveInteractionData logs and rethrows.
const count = (link) => saveInteractionData(auth.userData, null, 'links', link.id).catch(() => {});

// A link off the platform waits for them to go on; one on it opens as an ordinary new-tab link.
const leaving = ref(null);
function follow(event, link) {
  if (!leavesPlatform(link.link)) {
    count(link);
    return;
  }
  event.preventDefault();
  leaving.value = link;
}
function goOn() {
  const link = leaving.value;
  leaving.value = null;
  window.open(link.link, '_blank', 'noopener,noreferrer');
  count(link);
}
</script>

<template>
  <section class="quick" data-testid="quick-links">
    <div class="quick__sh">
      <h2>Quick links</h2>
      <span class="quick__ln" />
    </div>

    <div class="quick__card">
      <a
        v-for="link in rows"
        :key="link.id"
        :href="link.link"
        target="_blank"
        rel="noopener noreferrer"
        class="quick__row"
        data-testid="quick-link"
        @click="follow($event, link)"
      >
        <!-- XN-4775: the agency's picture for the link; with none, the link icon in the same tile
             (Hunter, 2026-10-16: the ticket's "no gap left for a missing one", so no empty tile). -->
        <img v-if="link.image_url" class="quick__img" :src="link.image_url" alt="" />
        <span v-else class="quick__img quick__img--icon" aria-hidden="true">
          <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" /></svg>
        </span>
        <span class="quick__t">
          <b>{{ link.title }}</b>
          <span v-if="link.description" data-testid="quick-link-description">{{ link.description }}</span>
        </span>
        <span class="quick__domain" data-testid="quick-link-domain">{{ domainOf(link) }}</span>
        <span class="quick__sr">(opens in a new tab)</span>
        <svg class="quick__ext" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" /></svg>
      </a>
      <button
        v-if="!expanded && links.length > SHOWN"
        type="button"
        class="quick__more"
        data-testid="quick-links-more"
        @click="expanded = true"
      >Show all {{ links.length }}</button>
    </div>

    <XlModal v-if="leaving" title="You're leaving the portal" max-width="440px" data-testid="leaving" @close="leaving = null">
      <p class="quick__leave">
        {{ leaving.title }} is on <b>{{ domainOf(leaving) }}</b>. It opens in a new tab, so you won't lose your place here.
      </p>
      <template #actions>
        <XlButton variant="ghost" data-testid="leaving-stay" @click="leaving = null">Stay here</XlButton>
        <XlButton data-testid="leaving-go" autofocus @click="goOn">Go to {{ domainOf(leaving) }}</XlButton>
      </template>
    </XlModal>
  </section>
</template>

<style scoped>
/* Prototype `.sh` (build 30 Sep 12:40), as Coming up above it: 34px above each header. */
.quick__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.quick__sh h2 {
  margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--xl-color-outline-variant);
}
.quick__ln { flex: 1; height: 1px; background: var(--xl-color-border); }

/* Prototype `.card` with `.row`s. */
.quick__card {
  overflow: hidden; background: #fff; border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); box-shadow: var(--xl-shadow-1);
}
.quick__row {
  display: flex; align-items: center; gap: 14px; padding: 14px 18px;
  border-bottom: 1px solid var(--xl-color-border-subtle); color: inherit; text-decoration: none;
  transition: background 0.12s;
}
.quick__row:last-child { border-bottom: 0; }
.quick__row:hover { background: var(--xl-color-primary-container); }
.quick__row:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: -2px; }
.quick__img { width: 46px; height: 46px; flex: 0 0 46px; border-radius: 11px; object-fit: cover; display: block; border: 1px solid var(--xl-color-border); }
/* The staff preview's tile (LinkModal), so the editor shows what lands here. */
.quick__img--icon {
  display: grid; place-items: center; border-color: transparent;
  background: color-mix(in srgb, var(--xl-color-primary) 8%, white); color: var(--xl-color-primary);
}
.quick__t { flex: 1; min-width: 0; }
.quick__t b { display: block; font-size: 15px; letter-spacing: -0.012em; overflow-wrap: anywhere; }
.quick__t span { display: block; margin-top: 1px; font-size: 12.5px; color: var(--xl-color-outline); overflow-wrap: anywhere; }
.quick__domain { flex: 0 1 auto; min-width: 0; font-size: 12.5px; color: var(--xl-color-outline); overflow-wrap: anywhere; text-align: right; }
.quick__ext { flex: 0 0 auto; color: var(--xl-color-primary); }
.quick__sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
.quick__more {
  display: block; width: 100%; padding: 13px 18px; border: 0; background: none; font: inherit; font-size: 14px; font-weight: 600; color: var(--xl-color-primary); text-align: left; cursor: pointer;
}
.quick__more:hover { background: var(--xl-color-primary-container); }
.quick__more:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: -2px; }
.quick__leave { margin: 0; font-size: 15px; line-height: 1.55; color: var(--xl-color-on-surface-variant); overflow-wrap: anywhere; }
.quick__leave b { color: var(--xl-color-on-surface); }

/* On a phone the domain moves under the title rather than squeezing it. */
/* The 60px in the next two rules is tile 46px + row gap 14px. */
@media (max-width: 480px) {
  .quick__row { flex-wrap: wrap; row-gap: 4px; }
  .quick__t { flex-basis: calc(100% - 60px - 30px); }
  .quick__domain { order: 3; flex-basis: 100%; padding-left: 60px; text-align: left; }
}
</style>
